'use client';

/**
 * DistanceCurrencyToggle.tsx
 * Currency toggle + reactive above-button price display + buy CTA.
 *
 * R3 changes (2026-05-21):
 * - USD price: $19 → $20
 * - Above-button price block moved INTO this component so it reacts to toggle state.
 *   Single source of truth: currency state drives both the price row AND the button label.
 * - EUR mode: operative €18, strike-through €28, "· launch price" label.
 * - USD mode: operative $20, strike-through $30, "· launch price" label.
 *
 * Pattern: same as /pro-member — the server parent resolves the currency
 * (lib/shop-currency.ts: pf_currency_choice cookie, else CF-IPCountry) and passes
 * initialCurrency; a toggle click writes the cookie.
 *
 * Checkout URLs:
 *   EUR: https://store.pianofantasy.com/checkout-distance-piano-solo-euro
 *   USD: https://store.pianofantasy.com/distance-score-usd
 */

import { useEffect, useState } from 'react';
import CheckoutModal from '@/components/checkout/CheckoutModal';
import { clearLegacyCurrency, writeCurrencyCookie } from '@/lib/currency-cookie';
import { DISTANCE_DISPLAY } from '@/lib/shop-prices';

type Currency = 'EUR' | 'USD';

interface Props {
  /** Resolved on the server by lib/shop-currency.ts (cookie, else IP default) —
   *  the same decision /pro-member makes, so the first paint is already right. */
  initialCurrency: Currency;
}

// Display strings live in lib/shop-prices.ts (2026-10-05) — the same ones the
// /store listing uses, so the two can never show different prices.

// checkoutEur / checkoutUsd (GHL links) were retired on 2026-08-07 when the Buy
// button became the Stripe pop-up cart (CheckoutModal, mode="one-time"). They were kept on
// the interface "for back-compat" and that is how the dead URLs survived into
// the Product JSON-LD for eighteen days. Removed 2026-08-25.
export default function DistanceCurrencyToggle({ initialCurrency }: Props) {
  const [currency, setCurrency] = useState<Currency>(initialCurrency);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // 2026-10-05: the browser never re-decides on load — the server already
  // applied the choice (lib/shop-currency.ts, same as /pro-member). The retired
  // `pf_currency` cookie and `pf-currency` localStorage key are cleared.
  useEffect(() => {
    clearLegacyCurrency();
  }, []);

  function handleSelect(c: Currency) {
    setCurrency(c);
    writeCurrencyCookie(c);
  }

  const { price, reference: priceReference } = DISTANCE_DISPLAY[currency];

  return (
    <div className="sv2-distance__cta-block">
      {/* Reactive above-button price display — subscribes to same currency state */}
      {/* EUR mode: €18 operative + €28 strike-through + "· launch price" */}
      {/* USD mode: $20 operative + $30 strike-through + "· launch price" */}
      <div className="sv2-distance__price-row" aria-live="polite" aria-atomic="true">
        <span className="sv2-distance__price-operative">{price}</span>
        <span className="sv2-distance__price-reference">{priceReference}</span>
        <span className="sv2-distance__price-label">&middot; launch price</span>
      </div>

      {/* Currency toggle — pill pattern matching Pro Membership (pm3 + pm11 CSS convention):
          - Container: pill wrapper (border-radius: 999px)
          - Inactive: transparent bg, thin gold border, muted text
          - Active: gold fill, dark text
          - Labels: "EUR" / "USD" — uppercase, no currency symbols, no separator */}
      <div className="sv2-distance__currency-toggle" role="group" aria-label="Select currency">
        <button
          type="button"
          className={`sv2-distance__toggle-btn${currency === 'EUR' ? ' sv2-distance__toggle-btn--active' : ''}`}
          data-currency="EUR"
          aria-pressed={currency === 'EUR'}
          onClick={() => handleSelect('EUR')}
        >
          EUR
        </button>
        <button
          type="button"
          className={`sv2-distance__toggle-btn${currency === 'USD' ? ' sv2-distance__toggle-btn--active' : ''}`}
          data-currency="USD"
          aria-pressed={currency === 'USD'}
          onClick={() => handleSelect('USD')}
        >
          USD
        </button>
      </div>

      {/* Buy CTA — opens the Stripe pop-up cart (2026-08-07) */}
      <button
        type="button"
        onClick={() => setCheckoutOpen(true)}
        className="btn btn-primary sv2-distance__cta"
      >
        Buy Distance &mdash; {price}
      </button>
      <p className="sv2-distance__cta-footnote">One-time purchase · Instant download · Digital score (PDF)</p>

      {checkoutOpen && (
        <CheckoutModal
          mode="one-time"
          productName="Distance for Piano Solo"
          coverImage="/images/store/distance-cover.webp"
          description="An advanced concert work for solo piano by Lars Nelissen — the complete digital score (PDF)."
          currency={currency}
          onCurrencyChange={handleSelect}
          eur={{ priceId: 'distance_score_eur', unitAmount: 1800 }}
          usd={{ priceId: 'distance_score_usd', unitAmount: 2000 }}
          onClose={() => setCheckoutOpen(false)}
        />
      )}
    </div>
  );
}
